import {useMemo} from 'react';

import {type RunHistoryRow} from '../../../types/run';
import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {
  type MediaCount,
  type MediaKey,
  type RunName,
  type SliderStep,
  type StepMeta,
  type StepMetaRun,
} from '../types';
import {
  calculateStepBounds,
  resolveSliderStepCollisions,
  type SliderStepMapping,
} from '../utils/stepUtils';

// History rows can hold NaN/±Infinity or the "NaN"/"Infinity" strings the
// backend stores for non-finite floats, none of which work as slider values.
function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function computeStepMeta(
  runsWithMedia: RunWithHistoryAndMediaWandb[],
  mediaSliderKey: string,
  runStepsMap: Map<string, RunHistoryRow[]>,
  mediaKeys?: string[]
): StepMeta {
  // Early return if no runs with media
  if (runsWithMedia.length === 0) {
    return {
      runs: [],
      min: 0,
      max: 0,
      steps: [],
      stepSet: new Set<number>(),
      sliderKey: mediaSliderKey,
      minSliderStep: 0,
      maxSliderStep: 0,
      sliderSteps: [],
      sliderStepSet: new Set<number>(),
      minIndex: 0,
      maxIndex: 0,
      indices: [],
      indexSet: new Set<number>(),
      stepsByRunKey: new Map(),
      mediaCountByRunKeyStep: new Map(),
    };
  }

  let globalStepMin = Infinity;
  let globalStepMax = -Infinity;
  let globalIndexMin = Infinity;
  let globalIndexMax = -Infinity;
  const globalSteps = new Set<number>();
  const globalSliderSteps = new Set<number>();
  const globalIndices = new Set<number>();
  const globalStepsBySliderStep = new Map<number, number>();

  // Accumulated while iterating per-run history rows
  const stepsByRunKey = new Map<string, Map<string, number[]>>();
  const mediaCountByRunKeyStep = new Map<
    RunName,
    Map<MediaKey, Map<SliderStep, MediaCount>>
  >();

  // Build run-level data while recording global stats
  const runs = runsWithMedia.map(runHistory => {
    const steps = runStepsMap.get(runHistory.name);

    if (!steps) {
      return {
        id: runHistory.name,
        min: 0,
        max: 0,
        steps: [] as number[],
        stepSet: new Set<number>(),
        minSliderStep: 0,
        maxSliderStep: 0,
        sliderSteps: [] as number[],
        sliderStepSet: new Set<number>(),
        sliderKey: mediaSliderKey,
        minIndex: 0,
        maxIndex: 0,
        indices: [] as number[],
        indexSet: new Set<number>(),
      };
    }

    // Create step to slider step mappings
    const mappings: SliderStepMapping[] = [];
    for (const history of steps) {
      const sliderStep = history[mediaSliderKey];
      if (isFiniteNumber(sliderStep)) {
        mappings.push({step: history._step, sliderStep});
      }
    }
    // A key with no finite values gets a single mapping at the run's latest
    // step: the slider hides for a lone tick and the panel pins to the
    // latest media.
    if (mappings.length === 0 && steps.length > 0) {
      const latestStep = steps.reduce(
        (max, history) => Math.max(max, history._step as number),
        -Infinity
      );
      mappings.push({step: latestStep, sliderStep: latestStep});
    }

    // Resolve collisions using the most recent step value
    const {uniqueSliderSteps, stepsBySliderStep, resolvedSteps} =
      resolveSliderStepCollisions(mappings, 'latest');

    const {localResolveMin, localResolvedMax} =
      calculateStepBounds(resolvedSteps);
    const {
      localResolveMin: localSliderResolveMin,
      localResolvedMax: localSliderResolvedMax,
    } = calculateStepBounds(uniqueSliderSteps);

    // Single pass over raw history rows: collect indices, stepsByRunKey, and
    // mediaCountByRunKeyStep. Previously these were two separate loops.
    let runIndices: number[] = [];
    let runIndexMin = Infinity;
    let runIndexMax = -Infinity;

    if (mediaKeys && mediaKeys.length > 0) {
      const allIndices = new Set<number>();
      const runKeyStepSets = new Map<string, Set<number>>();
      const runKeyStepCounts = new Map<MediaKey, Map<SliderStep, MediaCount>>();

      // Use runHistory.history (full history rows with all logged fields) rather
      // than runStepsMap rows, which are filtered/collision-resolved and may not
      // carry media key values in test setups.
      for (const row of runHistory.history ?? []) {
        const step = row._step as number;
        const rawSliderValue =
          mediaSliderKey !== '_step' ? row[mediaSliderKey] : step;
        const sliderStep = isFiniteNumber(rawSliderValue)
          ? rawSliderValue
          : step;

        for (const key of mediaKeys) {
          const val = row[key];
          if (val != null && typeof val === 'object') {
            const count = (val.count ?? 1) as number;
            const grouping = (val.grouping || 1) as number;
            const mediaCount = Math.floor(count / grouping);

            for (let i = 0; i < mediaCount; i++) {
              allIndices.add(i);
            }

            if (!runKeyStepSets.has(key)) {
              runKeyStepSets.set(key, new Set());
            }
            runKeyStepSets.get(key)!.add(sliderStep);

            if (!runKeyStepCounts.has(key)) {
              runKeyStepCounts.set(key, new Map());
            }
            const stepCountMap = runKeyStepCounts.get(key)!;
            stepCountMap.set(
              sliderStep,
              Math.max(stepCountMap.get(sliderStep) ?? 0, mediaCount)
            );
          }
        }
      }

      runIndices = Array.from(allIndices).sort((a, b) => a - b);
      if (runIndices.length > 0) {
        runIndexMin = runIndices[0];
        runIndexMax = runIndices[runIndices.length - 1];
      }

      // Convert step Sets to sorted arrays
      const sortedKeyMap = new Map<string, number[]>();
      for (const [key, stepSet] of runKeyStepSets) {
        sortedKeyMap.set(
          key,
          Array.from(stepSet).sort((a, b) => a - b)
        );
      }
      stepsByRunKey.set(runHistory.name, sortedKeyMap);
      mediaCountByRunKeyStep.set(runHistory.name, runKeyStepCounts);
    }

    const runStats: StepMetaRun = {
      id: runHistory.name,
      min: localResolveMin === Infinity ? 0 : localResolveMin,
      max: localResolvedMax === -Infinity ? 0 : localResolvedMax,
      steps: resolvedSteps,
      stepSet: new Set(resolvedSteps),
      minSliderStep:
        localSliderResolveMin === Infinity ? 0 : localSliderResolveMin,
      maxSliderStep:
        localSliderResolvedMax === -Infinity ? 0 : localSliderResolvedMax,
      sliderSteps: uniqueSliderSteps,
      sliderStepSet: new Set(uniqueSliderSteps),
      sliderKey: mediaSliderKey,
      sliderStepMap: stepsBySliderStep,
      minIndex: runIndexMin === Infinity ? 0 : runIndexMin,
      maxIndex: runIndexMax === -Infinity ? 0 : runIndexMax,
      indices: runIndices,
      indexSet: new Set(runIndices),
    };

    // Aggregate from raw bounds: an empty run's ±Infinity is a min/max
    // no-op, while its normalized 0 would wrongly extend the global range
    // (e.g. drag the min to 0 for a run whose steps start above 0).
    globalStepMin = Math.min(globalStepMin, localResolveMin);
    globalStepMax = Math.max(globalStepMax, localResolvedMax);
    globalIndexMin = Math.min(globalIndexMin, runStats.minIndex);
    globalIndexMax = Math.max(globalIndexMax, runStats.maxIndex);
    resolvedSteps.forEach(s => globalSteps.add(s));
    uniqueSliderSteps.forEach(s => globalSliderSteps.add(s));
    runIndices.forEach(i => globalIndices.add(i));

    // Update global step mapping
    stepsBySliderStep.forEach((step, sliderStep) => {
      if (
        !globalStepsBySliderStep.has(sliderStep) ||
        globalStepsBySliderStep.get(sliderStep)! < step
      ) {
        globalStepsBySliderStep.set(sliderStep, step);
      }
    });

    return runStats;
  });

  const minStep = globalStepMin === Infinity ? 0 : globalStepMin;
  const maxStep = globalStepMax === -Infinity ? 0 : globalStepMax;

  const allSliderSteps = Array.from(globalSliderSteps).sort((a, b) => a - b);
  const allSteps = Array.from(globalSteps).sort((a, b) => a - b);
  const allIndices = Array.from(globalIndices).sort((a, b) => a - b);

  return {
    runs,
    min: minStep,
    max: maxStep,
    steps: allSteps,
    stepSet: new Set(allSteps),
    sliderKey: mediaSliderKey,
    minSliderStep: allSliderSteps[0] ?? 0,
    maxSliderStep: allSliderSteps[allSliderSteps.length - 1] ?? 0,
    sliderSteps: allSliderSteps,
    sliderStepSet: new Set(allSliderSteps),
    minIndex: globalIndexMin === Infinity ? 0 : globalIndexMin,
    maxIndex: globalIndexMax === -Infinity ? 0 : globalIndexMax,
    indices: allIndices,
    indexSet: new Set(allIndices),
    stepsByRunKey,
    mediaCountByRunKeyStep,
  };
}

export function useStepMeta(
  runsWithMedia: RunWithHistoryAndMediaWandb[],
  mediaSliderKey: string,
  runStepsMap: Map<string, RunHistoryRow[]>,
  mediaKeys?: string[]
): StepMeta {
  return useMemo(
    () =>
      computeStepMeta(runsWithMedia, mediaSliderKey, runStepsMap, mediaKeys),
    [runsWithMedia, mediaSliderKey, runStepsMap, mediaKeys]
  );
}
